import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";

export type SeoOverrideEntry = {
  title?: string;
  description?: string;
  schemas?: unknown[];
};

type OverridesMap = Record<string, SeoOverrideEntry>;

async function fetchOverrides(): Promise<OverridesMap> {
  const { data } = await supabase
    .from("site_settings")
    .select("setting_value")
    .eq("setting_key", "seo_overrides")
    .maybeSingle();
  return (data?.setting_value as OverridesMap) ?? {};
}

export function useSeoOverride(path: string) {
  const { data } = useQuery({
    queryKey: ["seo-overrides"],
    queryFn: fetchOverrides,
    staleTime: 5 * 60 * 1000,
  });
  return data?.[path] ?? null;
}

/**
 * Renders an extra <Helmet> that overrides title/description and appends extra
 * JSON-LD schemas when an override exists for the given path. Multiple Helmets
 * merge — the last meta with a given name wins.
 */
export function SeoOverride({ path }: { path: string }) {
  const override = useSeoOverride(path);
  if (!override) return null;
  return (
    <Helmet>
      {override.title && <title>{override.title}</title>}
      {override.title && <meta property="og:title" content={override.title} />}
      {override.description && (
        <meta name="description" content={override.description} />
      )}
      {override.description && (
        <meta property="og:description" content={override.description} />
      )}
      {override.schemas?.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
